/**
 * map_structures — what the World Atlas shows at BUILDING ZOOM, and how it is found (2026-09-24). Pure: no DOM, no three.
 *
 * "Zoom in enough to see individual buildings and structures in cities and towns, with names of what each structure
 * is … an advanced search for specific structures on the world map."
 *
 *   THE MAP FILE (`public/maps/<fnv(seed)>.json`, verify/bake-proxies.mts --map): for every settlement, every building
 *   the town really assembles — resolved by the SAME `resolveProxyPlacements` the far tier and the assembly share —
 *   with its true ground outline (the prefab's lowest silhouette band × the assembly scale, turned by its yaw), its
 *   height, the plan's FUNCTION and building TYPE (the names), its model; and the plan's streets and gates. Loaded
 *   lazily when the map first opens (the proxies file every client fetches is untouched).
 *   NAMES: a building is named by what it is FOR — the plan's function read through its type ("medical" + "shop" = a
 *   Clinic, "habitation" + "skyscraper" = a Residential Tower); landmarks by their monument.
 *   THE INDEX: every named thing on the map — buildings, landmarks, ports, cemeteries, gates, town venues, player
 *   structures, platform-built structures, guild halls, player cities — one flat list the Find panel queries (text
 *   tokens against the name, category, settlement and model; category and civ filters; a distance scope; sorted by
 *   distance, name or size).
 */

// ── The map file ──────────────────────────────────────────────────────────────────────────────────────────────────
export const MAP_FORMAT_VERSION = 1;
/** Numbers per building: lx, lz (outline centre, site-local), w, d, yaw, h, fnIdx, typeIdx, kind (0 building, 1 landmark), keyIdx. */
export const MAP_B_STRIDE = 10;
export interface MapSite {
  id: number;
  cx: number;
  cz: number;
  /** Buildings, MAP_B_STRIDE each. */
  b: number[];
  /** Street edges: [n, halfWidth, classIdx, x0, z0, … x(n-1), z(n-1)] repeated (site-local). */
  roads: number[];
  /** Gates: [x, z, main (0|1)] repeated (site-local). */
  gates: number[];
}
export interface MapFile { v: number; seed: string; keys: string[]; fns: string[]; types: string[]; roadClasses: string[]; sites: MapSite[] }

export function mapFileFor(seed: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return `${(h >>> 0).toString(16)}.json`;
}

/** Why a parsed file cannot be used, or null. */
export function mapFileProblem(f: unknown, seed: string): string | null {
  const m = f as Partial<MapFile> | null;
  if (!m || typeof m !== 'object') return 'not an object';
  if (m.v !== MAP_FORMAT_VERSION) return `format v${String(m.v)} (want v${MAP_FORMAT_VERSION})`;
  if (m.seed !== seed) return 'baked for another world seed';
  if (!Array.isArray(m.sites) || !Array.isArray(m.keys) || !Array.isArray(m.fns) || !Array.isArray(m.types)) return 'missing tables';
  return null;
}

// ── Categories ────────────────────────────────────────────────────────────────────────────────────────────────────
export type MapCategory =
  | 'residential' | 'commerce' | 'industry' | 'energy' | 'farming' | 'science' | 'civic' | 'health' | 'military' | 'leisure'
  | 'landmark' | 'port' | 'cemetery' | 'venue' | 'gate' | 'player' | 'city' | 'placed' | 'guild';

/** Map colours are DATA (a category's identity on every theme), like the map's contact kinds — not theme chrome. */
export const CATEGORY_INFO: Record<MapCategory, { label: string; colour: string }> = {
  residential: { label: 'Homes', colour: '#8fb0d8' },
  commerce: { label: 'Trade', colour: '#e3b75b' },
  industry: { label: 'Industry', colour: '#a39688' },
  energy: { label: 'Power & utilities', colour: '#e07a4a' },
  farming: { label: 'Farming', colour: '#7fbf6a' },
  science: { label: 'Science', colour: '#6fd1d6' },
  civic: { label: 'Civic', colour: '#c9a6ff' },
  health: { label: 'Medical', colour: '#ff7d8a' },
  military: { label: 'Military', colour: '#d9544d' },
  leisure: { label: 'Leisure', colour: '#f08fd0' },
  landmark: { label: 'Landmarks', colour: '#fff1b8' },
  port: { label: 'Ports & terminals', colour: '#58c7ff' },
  cemetery: { label: 'Cemeteries', colour: '#b7bcc4' },
  venue: { label: 'Squares & venues', colour: '#ffd07a' },
  gate: { label: 'Gates', colour: '#d6d0c4' },
  player: { label: 'Player structures', colour: '#b98cff' },
  city: { label: 'Player cities', colour: '#d9a441' },
  placed: { label: 'Town works', colour: '#e8d9b0' },
  guild: { label: 'Guild halls', colour: '#7fe0b0' },
};
export const CATEGORY_ORDER: MapCategory[] = [
  'residential', 'commerce', 'industry', 'energy', 'farming', 'science', 'civic', 'health', 'military', 'leisure',
  'landmark', 'port', 'cemetery', 'venue', 'gate', 'player', 'city', 'placed', 'guild',
];

const FN_CATEGORY: Record<string, MapCategory> = {
  habitation: 'residential', habitation_luxury: 'residential',
  market: 'commerce', commerce: 'commerce', corporate: 'commerce',
  manufacturing: 'industry', industry: 'industry', storage: 'industry', docking: 'industry', repair: 'industry',
  fuel: 'energy', reactor: 'energy', utility: 'energy', lifesupport: 'energy',
  agriculture: 'farming',
  science: 'science', datacenter: 'science',
  civic: 'civic', command: 'civic', customs: 'civic', landing: 'civic', airlock: 'civic',
  medical: 'health',
  barracks: 'military', defense: 'military', armory: 'military',
  entertainment: 'leisure', recreation: 'leisure',
};
export function categoryOfFunction(fn: string): MapCategory { return FN_CATEGORY[fn] ?? 'civic'; }

// ── Names ─────────────────────────────────────────────────────────────────────────────────────────────────────────
const title = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** A building's name from what it is FOR (the plan's function) read through its building type. */
const FN_NAMES: Record<string, (type: string) => string> = {
  habitation: (t) => (t === 'apartment' ? 'Apartment Block' : t === 'skyscraper' ? 'Residential Tower' : t === 'house' ? 'House' : 'Residence'),
  habitation_luxury: (t) => (t === 'house' ? 'Villa' : t === 'apartment' ? 'Luxury Apartments' : t === 'skyscraper' ? 'Luxury Tower' : 'Manor'),
  market: (t) => (t === 'skyscraper' ? 'Trade Tower' : t === 'shop' ? 'Market Hall' : 'Market'),
  commerce: (t) => (t === 'shop' ? 'Shop' : t === 'skyscraper' ? 'Commerce Tower' : t === 'office' ? 'Trading House' : 'Trade House'),
  corporate: (t) => (t === 'skyscraper' ? 'Corporate Tower' : 'Corporate Office'),
  manufacturing: (t) => (t === 'factory' ? 'Workshop' : t === 'warehouse' ? 'Fabrication Depot' : 'Fabrication Works'),
  industry: (t) => (t === 'factory' ? 'Foundry' : t === 'warehouse' ? 'Industrial Depot' : 'Industrial Works'),
  storage: (t) => (t === 'warehouse' ? 'Warehouse' : 'Storehouse'),
  docking: (t) => (t === 'warehouse' ? 'Dock Warehouse' : 'Docking Bay'),
  repair: () => 'Repair Shop',
  fuel: () => 'Fuel Depot',
  reactor: () => 'Power Plant',
  utility: () => 'Utility Station',
  lifesupport: () => 'Life Support Plant',
  agriculture: (t) => (t === 'warehouse' ? 'Granary' : t === 'factory' ? 'Hydroponics Farm' : t === 'house' ? 'Farmstead' : 'Farm'),
  science: (t) => (t === 'office' ? 'Research Office' : t === 'skyscraper' ? 'Research Tower' : 'Laboratory'),
  datacenter: () => 'Data Center',
  civic: (t) => (t === 'skyscraper' ? 'Civic Tower' : t === 'office' ? 'Civic Office' : 'Civic Hall'),
  command: () => 'Command Center',
  customs: () => 'Customs House',
  landing: () => 'Landing Office',
  airlock: () => 'Airlock',
  medical: (t) => (t === 'skyscraper' || t === 'apartment' ? 'Hospital' : 'Clinic'),
  barracks: () => 'Barracks',
  defense: () => 'Guard Post',
  armory: () => 'Armory',
  entertainment: (t) => (t === 'shop' ? 'Cantina' : t === 'skyscraper' ? 'Entertainment Tower' : 'Entertainment Hall'),
  recreation: () => 'Recreation Hall',
};
export function buildingName(fn: string, type: string): string {
  const f = FN_NAMES[fn];
  return f ? f(type) : `${title(fn || 'building')}${type ? ` ${title(type)}` : ''}`;
}

// ── Venue names (the interiors programme, slice E, 2026-10-01) ────────────────────────────────────────────────────
/**
 * A building BOUND to a town venue (lib/venue_binding.ts — the one building of a town that is its tavern, its clinic)
 * is named for the venue on every map: "The Radiant Cup" / "The Frosthearth" / "Clinic". The namer is registered by
 * `AtlasVenueModule` (it answers null while `platform_flag interior_venues_v1` is off); with none registered every
 * name is what it always was. `index` = the building's place in its site's baked list. globalThis-backed (the
 * instance-split law).
 */
export type MapVenueNamer = (siteId: number, index: number) => { name: string; category: MapCategory; detail: string } | null;
const GMV = globalThis as unknown as { __atlasMapVenueNamer?: MapVenueNamer | null };
export function setMapVenueNamer(fn: MapVenueNamer | null): void { GMV.__atlasMapVenueNamer = fn; }
/** The venue a baked building is, or null. */
export function mapVenueOf(siteId: number, index: number): { name: string; category: MapCategory; detail: string } | null {
  const fn = GMV.__atlasMapVenueNamer;
  return fn ? fn(siteId, index) : null;
}

/** The civic monuments a settlement raises (pickLandmark's pools). */
const LANDMARK_NAMES: Record<string, string> = {
  leasure_structure: 'Leisure Pavilion', survey_structure: 'Survey Spire', storage_structure: 'Grand Depot',
  mining_facility: 'Mining Facility', output: 'Output Works', outpost: 'Outpost Tower',
};
/** 'structure:civa_scifi7' → 'Scifi 7' (the model a building wears — the hover card's detail, never its name). */
export function prettyModel(key: string): string {
  const slug = key.replace(/^(structure|prop|decor|foliage):/, '').replace(/^civ[ab]_/, '');
  if (LANDMARK_NAMES[slug]) return LANDMARK_NAMES[slug];
  return title(slug.replace(/(\D)(\d)/, '$1 $2'));
}
export function landmarkName(key: string): string {
  const slug = key.replace(/^structure:/, '').replace(/^civ[ab]_/, '');
  // The Borean pools raise whole civic blocks (city3, city4, city16) as their monument: named for the role, not the file.
  if (/^city\d+[a-z]?$/.test(slug)) return 'Civic Monument';
  return LANDMARK_NAMES[slug] ?? `Monument (${prettyModel(key)})`;
}

/** A platform-built (Edit Mode) structure's name: its model, with the whole civic blocks named for their role. */
export function placementName(key: string): string {
  const slug = key.replace(/^(structure|prop|decor):/, '').replace(/^civ[ab]_/, '');
  if (/^city\d+[a-z]?$/.test(slug)) return 'Civic Complex';
  if (/^scifi\d+[a-z]?$/.test(slug)) return 'Civic Building';
  return LANDMARK_NAMES[slug] ?? prettyModel(key);
}

// ── Geometry ──────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * A building's outline on the map from its prefab's lowest silhouette band — [y0, h, w, d, ox, oz] in natural units
 * about the bbox bottom-centre — scaled and turned by the placement (the ladderSolids convention: rotation.y = yaw).
 */
export function outlineOf(lx: number, lz: number, yaw: number, scale: number, band: number[]): { cx: number; cz: number; w: number; d: number } {
  const [, , w, d, ox, oz] = band;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return { cx: lx + (ox * c + oz * s) * scale, cz: lz + (-ox * s + oz * c) * scale, w: w * scale, d: d * scale };
}

/** The four corners of a w×d rectangle about (cx, cz) turned by three's rotation.y = yaw (local +x → (cos, −sin)). */
export function rectCorners(cx: number, cz: number, w: number, d: number, yaw: number): Array<[number, number]> {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const hw = w / 2, hd = d / 2;
  const at = (lx: number, lz: number): [number, number] => [cx + lx * c + lz * s, cz - lx * s + lz * c];
  return [at(-hw, -hd), at(hw, -hd), at(hw, hd), at(-hw, hd)];
}

/** Is (px, pz) inside the turned rectangle? (The hover test.) */
export function inRect(px: number, pz: number, cx: number, cz: number, w: number, d: number, yaw: number): boolean {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const dx = px - cx, dz = pz - cz;
  const lx = dx * c - dz * s; // the inverse of rectCorners' turn
  const lz = dx * s + dz * c;
  return Math.abs(lx) <= w / 2 && Math.abs(lz) <= d / 2;
}

// ── The pick grid ──────────────────────────────────────────────────────────────────────────────────────────────
/**
 * THE HOVER PICK'S GRID (2026-09-28, "fix the map … hover performance"): every pointer move scanned all ~6k glyphs
 * twice (a fresh concat each time). The grid buckets them once per index rebuild: a point glyph in its own 128 m cell,
 * an outlined one (w × d, any yaw) in every cell its bounding circle meets. Candidates come back as indices in
 * ascending order, so a first-match rule over them picks exactly what the linear scan picked.
 */
export interface PickGrid { cell: number; pts: Map<number, number[]>; outs: Map<number, number[]> }
const pickKey = (cx: number, cz: number): number => (cx + 32768) * 65536 + (cz + 32768);
export function buildPickGrid(glyphs: ReadonlyArray<{ x: number; z: number; w?: number; d?: number }>, cell = 128): PickGrid {
  const pts = new Map<number, number[]>(), outs = new Map<number, number[]>();
  const add = (m: Map<number, number[]>, k: number, i: number): void => { const l = m.get(k); if (l) l.push(i); else m.set(k, [i]); };
  glyphs.forEach((g, i) => {
    if (g.w !== undefined && g.d !== undefined) {
      // The outline's bounding circle plus the pick's 3 px pad at the smallest zoom a pick runs at (0.08 px/m → 37.5 m).
      const rr = Math.hypot(g.w, g.d) / 2 + 3 / 0.08;
      for (let cx = Math.floor((g.x - rr) / cell); cx <= Math.floor((g.x + rr) / cell); cx++) for (let cz = Math.floor((g.z - rr) / cell); cz <= Math.floor((g.z + rr) / cell); cz++) add(outs, pickKey(cx, cz), i);
    } else add(pts, pickKey(Math.floor(g.x / cell), Math.floor(g.z / cell)), i);
  });
  return { cell, pts, outs };
}
/** Point glyphs whose cell lies within `rM` of (wx, wz) — a superset of those within rM — ascending indices. */
export function pickPoints(g: PickGrid, wx: number, wz: number, rM: number): number[] {
  const out: number[] = [];
  for (let cx = Math.floor((wx - rM) / g.cell); cx <= Math.floor((wx + rM) / g.cell); cx++) for (let cz = Math.floor((wz - rM) / g.cell); cz <= Math.floor((wz + rM) / g.cell); cz++) {
    const l = g.pts.get(pickKey(cx, cz)); if (l) for (const i of l) out.push(i);
  }
  return out.sort((a, b) => a - b);
}
/** Outlined glyphs that may contain (wx, wz) — ascending indices. */
export function pickOutlines(g: PickGrid, wx: number, wz: number): readonly number[] {
  return g.outs.get(pickKey(Math.floor(wx / g.cell), Math.floor(wz / g.cell))) ?? [];
}

// ── Bearing ───────────────────────────────────────────────────────────────────────────────────────────────────────
const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
/** The world's north is −Z (the maps draw −Z up). */
export function compassOf(dx: number, dz: number): string {
  const a = Math.atan2(dx, -dz); // 0 = north, clockwise
  return COMPASS[((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8];
}
export function distanceText(m: number): string {
  if (!Number.isFinite(m)) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return m < 100_000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m / 1000)} km`;
}
export function bearingText(fromX: number, fromZ: number, x: number, z: number): string {
  const d = Math.hypot(x - fromX, z - fromZ);
  return d < 5 ? 'here' : `${distanceText(d)} ${compassOf(x - fromX, z - fromZ)}`;
}

// ── The index ─────────────────────────────────────────────────────────────────────────────────────────────────────
export interface MapEntry {
  /** Stable across rebuilds ('b:<site>:<i>', 'port:<site>:shuttle', 'st:<id>', 'city:<id>' …). */
  id: string;
  name: string;
  category: MapCategory;
  x: number;
  z: number;
  /** The settlement (or player city) it belongs to, '' when none. */
  place: string;
  /** 'a' | 'b' | '' — the settlement's civilisation. */
  civ: string;
  /** Extra searchable words (function, type, model, owner). */
  detail: string;
  /** Footprint area (m²) for the size sort; 0 when unknown. */
  area: number;
  /** The drawn outline, when it has one (three's rotation.y = yaw): a building, a precinct, a player structure. */
  w?: number;
  d?: number;
  yaw?: number;
  /** Height (m), when known. */
  h?: number;
  /** A radius (m) — a player city's border. */
  r?: number;
}

export interface MapQuery {
  text: string;
  categories: ReadonlySet<MapCategory>;
  civ: '' | 'a' | 'b';
  /** Only entries within this many metres of (fromX, fromZ); 0 = anywhere. */
  withinM: number;
  fromX: number;
  fromZ: number;
  sort: 'distance' | 'name' | 'size';
  limit: number;
}

/** Lower-case words of a text (the query and the haystack share it). */
export function tokens(text: string): string[] {
  return text.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 0);
}

/** An entry's search words, computed once per entry object (the index is rebuilt, never mutated). */
const HAY = new WeakMap<MapEntry, string[]>();
function hayOf(e: MapEntry): string[] {
  let h = HAY.get(e);
  if (!h) {
    h = tokens(`${e.name} ${CATEGORY_INFO[e.category].label} ${e.place} ${e.detail} ${e.civ === 'a' ? 'aurelian' : e.civ === 'b' ? 'borean' : ''}`);
    HAY.set(e, h);
  }
  return h;
}

/** Does an entry pass the query's text, civ and distance filters? (`cats` false = ignore the category filter.) */
function passes(e: MapEntry, want: readonly string[], q: MapQuery, w2: number, cats: boolean): number {
  if (cats && q.categories.size > 0 && !q.categories.has(e.category)) return -1;
  if (q.civ && e.civ !== q.civ) return -1;
  const dx = e.x - q.fromX, dz = e.z - q.fromZ;
  const d2 = dx * dx + dz * dz;
  if (w2 > 0 && d2 > w2) return -1;
  if (want.length > 0) {
    const hay = hayOf(e);
    for (const w of want) { if (!hay.some((h) => h.startsWith(w))) return -1; }
  }
  return d2;
}

/**
 * Query the index. Every query word must PREFIX-match a word of the entry (name, category label, place, detail) —
 * "clin" finds Clinics, "armory aur" the armories of Aurelian towns. Returns the total match count beside the page.
 */
export function queryMap(entries: readonly MapEntry[], q: MapQuery): { total: number; hits: MapEntry[] } {
  const want = tokens(q.text);
  const out: Array<{ e: MapEntry; d: number }> = [];
  const w2 = q.withinM > 0 ? q.withinM * q.withinM : 0;
  for (const e of entries) {
    const d2 = passes(e, want, q, w2, true);
    if (d2 >= 0) out.push({ e, d: d2 });
  }
  if (q.sort === 'name') out.sort((a, b) => NAME_ORDER.compare(a.e.name, b.e.name) || a.d - b.d);
  else if (q.sort === 'size') out.sort((a, b) => b.e.area - a.e.area || a.d - b.d);
  else out.sort((a, b) => a.d - b.d);
  return { total: out.length, hits: out.slice(0, Math.max(0, q.limit)).map((o) => o.e) };
}

/** One collator for every name sort (`localeCompare` builds its comparison state on each call). */
const NAME_ORDER = new Intl.Collator();

/**
 * THE FIND TAB'S ONE PASS (2026-09-28, "fix the map search … performance"): the chips' counts, the page and the map's
 * dots from ONE walk of the index and ONE sort — the tab used to walk ~39k entries three times (counts, the page, the
 * dots — the last two sorting the same list twice) on every refresh. `inView` narrows the walk ("in view" scope)
 * without building a filtered copy. Identical to `categoryCounts` + `queryMap` + `queryMap({limit: mapLimit})`.
 */
export function searchMap(entries: readonly MapEntry[], q: MapQuery, mapLimit: number, inView: ((e: MapEntry) => boolean) | null = null):
  { counts: Map<MapCategory, number>; total: number; hits: MapEntry[]; mapHits: MapEntry[] } {
  const want = tokens(q.text);
  const w2 = q.withinM > 0 ? q.withinM * q.withinM : 0;
  const counts = new Map<MapCategory, number>();
  const active = want.length > 0 || q.categories.size > 0;
  const out: Array<{ e: MapEntry; d: number }> = [];
  for (const e of entries) {
    if (inView && !inView(e)) continue;
    const d2 = passes(e, want, q, w2, false);
    if (d2 < 0) continue;
    counts.set(e.category, (counts.get(e.category) ?? 0) + 1);
    if (active && (q.categories.size === 0 || q.categories.has(e.category))) out.push({ e, d: d2 });
  }
  if (q.sort === 'name') out.sort((a, b) => NAME_ORDER.compare(a.e.name, b.e.name) || a.d - b.d);
  else if (q.sort === 'size') out.sort((a, b) => b.e.area - a.e.area || a.d - b.d);
  else out.sort((a, b) => a.d - b.d);
  const take = (n: number): MapEntry[] => { const k = Math.min(out.length, Math.max(0, n)); const r = new Array<MapEntry>(k); for (let i = 0; i < k; i++) r[i] = out[i].e; return r; };
  return { counts, total: out.length, hits: take(q.limit), mapHits: take(mapLimit) };
}

/** How many entries each category would add under the query's OTHER filters (the chips' counts). */
export function categoryCounts(entries: readonly MapEntry[], q: MapQuery): Map<MapCategory, number> {
  const want = tokens(q.text);
  const w2 = q.withinM > 0 ? q.withinM * q.withinM : 0;
  const out = new Map<MapCategory, number>();
  for (const e of entries) if (passes(e, want, q, w2, false) >= 0) out.set(e.category, (out.get(e.category) ?? 0) + 1);
  return out;
}

/** The baked buildings, landmarks and gates of the map file as index entries (site names and civs supplied). */
export function mapFileEntries(file: MapFile, placeOf: (siteId: number) => { name: string; civ: string } | null): MapEntry[] {
  const out: MapEntry[] = [];
  for (const s of file.sites) {
    const place = placeOf(s.id);
    if (!place) continue;
    for (let i = 0, n = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE, n++) {
      const kind = s.b[i + 8];
      const key = file.keys[s.b[i + 9]] ?? '';
      const fn = file.fns[s.b[i + 6]] ?? '';
      const type = file.types[s.b[i + 7]] ?? '';
      const landmark = kind === 1;
      const venue = mapVenueOf(s.id, n); // slice E: the town's tavern / clinic reads its venue name
      out.push({
        id: `b:${s.id}:${n}`,
        name: venue?.name ?? (landmark ? landmarkName(key) : buildingName(fn, type)),
        category: venue?.category ?? (landmark ? 'landmark' : categoryOfFunction(fn)),
        x: s.cx + s.b[i], z: s.cz + s.b[i + 1],
        place: place.name, civ: place.civ,
        detail: `${venue ? `${venue.detail} ` : ''}${landmark ? `monument ${prettyModel(key)}` : `${fn} ${type} ${prettyModel(key)}`}`,
        area: s.b[i + 2] * s.b[i + 3],
        w: s.b[i + 2], d: s.b[i + 3], yaw: s.b[i + 4], h: s.b[i + 5],
      });
    }
    for (let i = 0, n = 0; i + 3 <= s.gates.length; i += 3, n++) {
      out.push({ id: `gate:${s.id}:${n}`, name: s.gates[i + 2] ? 'Main Gate' : 'Gate', category: 'gate', x: s.cx + s.gates[i], z: s.cz + s.gates[i + 1], place: place.name, civ: place.civ, detail: 'gate entrance', area: 0 });
    }
  }
  return out;
}

/** SWG's city ranks (1..5). */
export const CITY_RANK_NAMES = ['Outpost', 'Village', 'Township', 'City', 'Metropolis'];
export function cityRankName(rank: number): string { return CITY_RANK_NAMES[Math.max(0, Math.min(4, Math.round(rank) - 1))]; }

/** A baked building in world metres (the local map draws these). */
export interface MapBuilding { x: number; z: number; w: number; d: number; yaw: number; h: number; name: string; category: MapCategory }

/** Every baked building within `r` of (x, z) — the local map's outlines and names. */
export function mapBuildingsNear(file: MapFile, x: number, z: number, r: number): MapBuilding[] {
  const out: MapBuilding[] = [];
  const r2 = r * r;
  for (const s of file.sites) {
    if (Math.hypot(s.cx - x, s.cz - z) > r + 800) continue; // a site's buildings stand within ~410 m of its centre
    for (let i = 0; i + MAP_B_STRIDE <= s.b.length; i += MAP_B_STRIDE) {
      const bx = s.cx + s.b[i], bz = s.cz + s.b[i + 1];
      if ((bx - x) ** 2 + (bz - z) ** 2 > r2) continue;
      const landmark = s.b[i + 8] === 1;
      const fn = file.fns[s.b[i + 6]] ?? '';
      const venue = mapVenueOf(s.id, i / MAP_B_STRIDE);
      out.push({
        x: bx, z: bz, w: s.b[i + 2], d: s.b[i + 3], yaw: s.b[i + 4], h: s.b[i + 5],
        name: venue?.name ?? (landmark ? landmarkName(file.keys[s.b[i + 9]] ?? '') : buildingName(fn, file.types[s.b[i + 7]] ?? '')),
        category: venue?.category ?? (landmark ? 'landmark' : categoryOfFunction(fn)),
      });
    }
  }
  return out;
}

// ── Label de-clutter ──────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Greedy label placement: labels come in priority order, each is kept only if its box overlaps no kept box.
 * Returns the indices kept. Boxes are [x, y, w, h] in screen pixels.
 */
export function declutter(boxes: ReadonlyArray<readonly [number, number, number, number]>, pad = 2): number[] {
  const kept: number[] = [];
  // A coarse grid keeps the overlap test near-linear for a few thousand labels.
  const CELL = 64;
  const grid = new Map<number, number[]>();
  const cellsOf = (b: readonly [number, number, number, number]): number[] => {
    const out: number[] = [];
    const x0 = Math.floor((b[0] - pad) / CELL), x1 = Math.floor((b[0] + b[2] + pad) / CELL);
    const y0 = Math.floor((b[1] - pad) / CELL), y1 = Math.floor((b[1] + b[3] + pad) / CELL);
    for (let gx = x0; gx <= x1; gx++) for (let gy = y0; gy <= y1; gy++) out.push(gx * 73856093 ^ gy * 19349663);
    return out;
  };
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    let hit = false;
    const cells = cellsOf(b);
    for (const c of cells) {
      for (const j of grid.get(c) ?? []) {
        const o = boxes[j];
        if (b[0] - pad < o[0] + o[2] && b[0] + b[2] + pad > o[0] && b[1] - pad < o[1] + o[3] && b[1] + b[3] + pad > o[1]) { hit = true; break; }
      }
      if (hit) break;
    }
    if (hit) continue;
    kept.push(i);
    for (const c of cells) { const arr = grid.get(c); if (arr) arr.push(i); else grid.set(c, [i]); }
  }
  return kept;
}
